/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components/ui/modal.js';
import { type Invoice } from '../../lib/api/finance.js';

import { CreditNoteForm } from './credit-note-form.js';
import { InvoiceForm } from './invoice-form.js';
import { PayNowModal } from './pay-now-modal.js';
import { PaymentForm } from './payment-form.js';

interface InvoiceModalsProps {
  readonly newOpen: boolean;
  readonly onNewOpenChange: (open: boolean) => void;
  readonly onCloseNew: () => void;
  readonly payInvoice: Invoice | null;
  readonly onClosePay: () => void;
  readonly payNowInvoice: Invoice | null;
  readonly onClosePayNow: () => void;
  readonly creditInvoice: Invoice | null;
  readonly onCloseCredit: () => void;
}

/** The New-Invoice, Record-Payment, Pay-Now and Credit-Note modals for the invoices view. */
export function InvoiceModals({
  newOpen,
  onNewOpenChange,
  onCloseNew,
  payInvoice,
  onClosePay,
  payNowInvoice,
  onClosePayNow,
  creditInvoice,
  onCloseCredit,
}: InvoiceModalsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <Modal
        open={newOpen}
        onOpenChange={onNewOpenChange}
        title={t('finance.invoices.add')}
        description={t('finance.invoices.addSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <InvoiceForm onDone={onCloseNew} />
      </Modal>
      <Modal
        open={payInvoice !== null}
        onOpenChange={onClosePay}
        title={t('finance.invoices.recordPayment')}
        description={t('finance.invoices.recordPaymentSubtitle')}
        closeLabel={t('common.cancel')}
      >
        {payInvoice !== null && <PaymentForm invoice={payInvoice} onDone={onClosePay} />}
      </Modal>
      <PayNowModal invoice={payNowInvoice} onClose={onClosePayNow} />
      <Modal
        open={creditInvoice !== null}
        onOpenChange={onCloseCredit}
        title={t('finance.creditNote.title')}
        description={t('finance.creditNote.subtitle')}
        closeLabel={t('common.cancel')}
      >
        {creditInvoice !== null && (
          <CreditNoteForm invoice={creditInvoice} onDone={onCloseCredit} />
        )}
      </Modal>
    </>
  );
}
